import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { useCallback, useMemo } from 'react';
import { useAIProcessingStatusWithMetadata } from '../../ai/useAIProcessingStatusWithMetadata';
import { useInputAutosave } from '../../autosave/hooks/useInputAutosave';
import { useIsHidingHighlySensitiveValue } from '../../dataSensitivity/useIsHidingHighlySensitiveValue';
import { useLeadStageChangeValidation } from '../../input/pipeline/hooks/leads/useLeadStageChangeValidation';
import { usePropertyValidation } from '../../validation/hooks/usePropertyValidation';
import useConditionalPropertiesPanel from '../hooks/useConditionalPropertiesPanel';
import { useIsPropertyReadOnly } from '../hooks/useIsPropertyReadOnly';
import { useTicketStatusChangeTracking } from '../hooks/useTicketStatusChangeTracking';
import useFirstCallEffect from './useFirstCallEffect';
import { Metrics } from '../../metrics/Metrics';
export const usePropertyInputFeatures = props => {
  const {
    accessLevel,
    conditionalPropertyRules,
    disabled: disabledProp,
    disableValidation,
    enableAutosave,
    enableConditionalPropertiesPanel,
    getAdditionalPropertyValue,
    inputMode,
    objectId,
    objectTypeId,
    onBlur,
    onChange,
    onFocus,
    onOpenChange,
    onSave,
    onValidationChange,
    property,
    propertyValue,
    readOnly: readOnlyProp,
    showAutoSaveText,
    use,
    validationSettings
  } = props;
  const onChangeWithFirstCallTracking = useFirstCallEffect(onChange, useCallback(() => {
    Metrics.counter('propertyinputv2-onchange', {
      type: property.type,
      fieldType: property.fieldType
    }).increment();
  }, [property.fieldType, property.type]));
  const onChangeWithTicketStatusTracking = useTicketStatusChangeTracking(onChangeWithFirstCallTracking);
  const {
    onBlur: wrappedOnBlur,
    onFocus: wrappedOnFocus,
    onValidationChange: wrappedOnValidationChange,
    onChange: wrappedOnChange,
    attemptAutosave,
    onOpenChange: wrappedOnOpenChange
  } = useInputAutosave({
    onBlur,
    onFocus,
    inputMode,
    onChange: onChangeWithTicketStatusTracking,
    onOpenChange,
    onSave,
    onValidationChange,
    enableAutosave,
    use
  });
  const {
    leadStageValidationModal,
    onChangeWithLeadStageValidation
  } = useLeadStageChangeValidation({
    onChange: wrappedOnChange,
    onApply: attemptAutosave,
    inputMode
  });
  const {
    conditionalPropertiesPanel,
    maybeOpenConditionalPropertiesPanel
  } = useConditionalPropertiesPanel({
    enableConditionalPropertiesPanel,
    conditionalPropertyRules,
    property,
    onApply: attemptAutosave,
    onChange: onChangeWithLeadStageValidation,
    objectId,
    objectTypeId,
    getAdditionalPropertyValue,
    showAutoSaveText,
    value: propertyValue
  });
  const isPropertyReadOnly = useIsPropertyReadOnly(objectTypeId, property, accessLevel, readOnlyProp);
  const shouldValidate = !disabledProp && !isPropertyReadOnly && !disableValidation &&
  // File properties cannot have custom validation rules.
  // Also, PropertyInputFilesV2 handles it's own validation using this framework.
  // Disabling validation from usePropertyValidation avoids race conditions.
  !isFile(property);
  const {
    onChangeSingle,
    onChangeMultiple,
    validationState
  } = usePropertyValidation({
    disableValidation: !shouldValidate,
    objectId,
    objectTypeId,
    onChange: maybeOpenConditionalPropertiesPanel,
    onValidationChange: wrappedOnValidationChange,
    property,
    propertyValue,
    validationSettings
  });
  const isHidingHighlySensitiveValue = useIsHidingHighlySensitiveValue();
  const {
    status: aiProcessingStatus
  } = useAIProcessingStatusWithMetadata(objectTypeId, property.name, objectId);
  return useMemo(() => ({
    // Core State
    aiProcessingStatus,
    isHidingHighlySensitiveValue,
    isPropertyReadOnly,
    shouldValidate,
    validationState,
    // Callbacks
    onChangeMultiple,
    onChangeSingle,
    wrappedOnBlur,
    wrappedOnFocus,
    wrappedOnOpenChange,
    wrappedOnValidationChange,
    // UI Elements / State
    conditionalPropertiesPanel,
    leadStageValidationModal
  }), [aiProcessingStatus, conditionalPropertiesPanel, isHidingHighlySensitiveValue, isPropertyReadOnly, leadStageValidationModal, onChangeMultiple, onChangeSingle, shouldValidate, validationState, wrappedOnBlur, wrappedOnFocus, wrappedOnOpenChange, wrappedOnValidationChange]);
};